import axios, { AxiosRequestConfig } from 'axios';
import { DataResponse, PagedResponse } from '../types/api';

const REVIEW_URL = '/v2/admin/risk-review';
const ENFORCEMENT_URL = '/v2/admin/risk-review/enforcement-mode';
const SCAN_URL = '/v2/admin/blocked-user-scans';
const BACKFILL_URL = '/v2/admin/blocked-user-signals/backfill';

export interface MatchedSignal {
  signalType: string;
  signalValue: string;
  blockedUserId: number;
  weight: number;
}

export interface BlockedUserSummary {
  id: number;
  fullName: string | null;
  email: string | null;
  signupDate: number | null;
  latestNotes: string[];
  blocked: boolean;
}

export interface RegistrationRiskLog {
  id: number;
  registrationEmail: string;
  normalizedEmail: string;
  ipAddress: string | null;
  phone: string | null;
  userAgent: string | null;
  riskScore: number;
  riskTier: string;
  matchedSignals: string | null;
  outcome: string;
  createdUserId: number | null;
  reviewed: boolean;
  reviewedBy: number | null;
  reviewedAt: string | null;
  reviewOutcome: string | null;
  created: string;
  blockedUserSummaries?: BlockedUserSummary[];
}

export interface RiskReviewFilter {
  riskTier?: string;
  reviewed?: boolean;
  outcome?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export type ReviewOutcome = 'CONFIRMED_BLOCK' | 'UNBLOCKED';

export type EnforcementMode = 'OFF' | 'ON';

export interface ScanResult {
  scanned: number;
  flagged: number;
  failed: number;
}

export interface BackfillResult {
  processed: number;
  failed: number;
}

class RiskReviewService {
  static list(
    params: RiskReviewFilter,
    config?: AxiosRequestConfig
  ): Promise<PagedResponse<RegistrationRiskLog>> {
    return axios.get(REVIEW_URL, { ...config, params });
  }

  static resolveReview(
    id: number,
    outcome: ReviewOutcome,
    config?: AxiosRequestConfig
  ) {
    return axios.patch(
      `${REVIEW_URL}/${id}/resolve`,
      { outcome },
      config
    );
  }

  static getEnforcementMode(
    config?: AxiosRequestConfig
  ): Promise<DataResponse<{ mode: EnforcementMode }>> {
    return axios.get(ENFORCEMENT_URL, config);
  }

  static setEnforcementMode(
    mode: EnforcementMode,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<{ mode: EnforcementMode }>> {
    return axios.put(ENFORCEMENT_URL, { mode }, config);
  }

  static triggerScan(
    params?: { startDate?: string; endDate?: string },
    config?: AxiosRequestConfig
  ): Promise<DataResponse<ScanResult>> {
    return axios.post(SCAN_URL, null, { ...config, params });
  }

  static getScanResults(
    params: RiskReviewFilter,
    config?: AxiosRequestConfig
  ): Promise<PagedResponse<RegistrationRiskLog>> {
    return axios.get(SCAN_URL, { ...config, params });
  }

  static backfillSignals(
    config?: AxiosRequestConfig
  ): Promise<DataResponse<BackfillResult>> {
    return axios.post(BACKFILL_URL, null, config);
  }
}

export { RiskReviewService };
